// Infrastructure Dashboard Component
// Displays real-time Kubernetes cluster health for platform admins
// Follows existing dashboard patterns - no synthetic data

import React, { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to format age in human-readable format
const formatAge = (seconds) => {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

// Status indicator component
const StatusIndicator = ({ status }) => {
  const colors = {
    healthy: 'bg-green-500',
    running: 'bg-green-500',
    degraded: 'bg-yellow-500',
    pending: 'bg-yellow-500',
    critical: 'bg-red-500',
    failed: 'bg-red-500',
    error: 'bg-red-500',
    unavailable: 'bg-gray-500'
  };

  return (
    <span className={`inline-block w-3 h-3 rounded-full ${colors[status?.toLowerCase()] || 'bg-gray-400'}`} />
  );
};

const InfrastructureDashboard = ({ currentUser }) => {
  const [healthData, setHealthData] = useState(null);
  const [events, setEvents] = useState(null);
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPod, setSelectedPod] = useState(null);
  const [podLogs, setPodLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch infrastructure health
  const fetchHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/infrastructure/health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Superadmin access required');
        }
        throw new Error(`Failed to fetch health: ${response.statusText}`);
      }

      const data = await response.json();
      setHealthData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Fetch K8s events
  const fetchEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/infrastructure/events?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }, []);

  // Fetch storage status
  const fetchStorage = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/infrastructure/storage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStorageData(data);
      }
    } catch (err) {
      console.error('Failed to fetch storage:', err);
    }
  }, []);

  // Fetch pod logs
  const fetchPodLogs = async (podName) => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/infrastructure/pods/${podName}/logs?lines=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPodLogs(data);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Restart deployment
  const restartDeployment = async (deploymentName) => {
    if (!window.confirm(`Restart deployment "${deploymentName}"? This will trigger a rolling restart.`)) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/infrastructure/deployments/${deploymentName}/restart`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert(`Deployment "${deploymentName}" restart initiated`);
        // Refresh health data after a short delay
        setTimeout(fetchHealth, 2000);
      } else {
        const error = await response.json();
        alert(`Failed to restart: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Initial load and auto-refresh
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchHealth(), fetchEvents(), fetchStorage()]);
      setLoading(false);
    };

    loadData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchHealth();
      fetchEvents();
      fetchStorage();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchHealth, fetchEvents, fetchStorage]);

  // Handle pod selection for logs
  useEffect(() => {
    if (selectedPod) {
      fetchPodLogs(selectedPod);
    } else {
      setPodLogs(null);
    }
  }, [selectedPod]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading infrastructure status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h3 className="text-red-800 font-semibold mb-2">Infrastructure Monitoring Unavailable</h3>
        <p className="text-red-600">{error}</p>
        {error.includes('Kubernetes') && (
          <p className="text-sm text-red-500 mt-2">
            Ensure the backend has kubernetes package installed and proper RBAC permissions.
          </p>
        )}
        <button
          onClick={() => { setError(null); fetchHealth(); }}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Infrastructure Health</h2>
          <p className="text-gray-600">
            Real-time Kubernetes cluster status for {healthData?.namespace || 'arse-platform'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            Last updated: {healthData?.timestamp ? new Date(healthData.timestamp).toLocaleTimeString() : 'N/A'}
          </span>
          <button
            onClick={() => { fetchHealth(); fetchEvents(); fetchStorage(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            disabled={loading}
          >
            <span className="mr-2">↻</span> Refresh
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={`p-4 rounded-lg ${
        healthData?.status === 'healthy' ? 'bg-green-50 border border-green-200' :
        healthData?.status === 'degraded' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center">
          <StatusIndicator status={healthData?.status} />
          <span className="ml-3 font-semibold capitalize">
            Cluster Status: {healthData?.status || 'Unknown'}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Pods */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Pods</h3>
          <div className="mt-2 flex items-baseline">
            <span className="text-3xl font-bold text-gray-900">
              {healthData?.pods?.running || 0}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              / {healthData?.pods?.total || 0} running
            </span>
          </div>
          {(healthData?.pods?.pending > 0 || healthData?.pods?.failed > 0) && (
            <div className="mt-1 text-sm">
              {healthData?.pods?.pending > 0 && (
                <span className="text-yellow-600 mr-2">{healthData.pods.pending} pending</span>
              )}
              {healthData?.pods?.failed > 0 && (
                <span className="text-red-600">{healthData.pods.failed} failed</span>
              )}
            </div>
          )}
        </div>

        {/* Deployments */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Deployments</h3>
          <div className="mt-2 flex items-baseline">
            <span className="text-3xl font-bold text-gray-900">
              {healthData?.deployments?.healthy || 0}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              / {healthData?.deployments?.total || 0} healthy
            </span>
          </div>
          {healthData?.deployments?.degraded > 0 && (
            <div className="mt-1 text-sm text-yellow-600">
              {healthData.deployments.degraded} degraded
            </div>
          )}
        </div>

        {/* Recent Events */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Recent Events</h3>
          <div className="mt-2 flex items-baseline">
            <span className="text-3xl font-bold text-gray-900">
              {events?.count || 0}
            </span>
          </div>
          {events?.events?.[0] && (
            <div className="mt-1 text-sm text-gray-500 truncate">
              {events.events[0].reason}: {events.events[0].message?.substring(0, 30)}...
            </div>
          )}
        </div>

        {/* Storage Status */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Storage</h3>
          <div className="mt-2">
            <span className="text-xl font-bold text-gray-900">
              {storageData?.provider || 'Loading...'}
            </span>
          </div>
          {storageData && (
            <div className="mt-1 text-sm text-gray-500">
              {storageData.type === 'gcs' && (
                <span className="truncate block">{storageData.bucket}</span>
              )}
              {storageData.type === 's3' && (
                <span className="truncate block">{storageData.bucket}</span>
              )}
              {storageData.type === 'local' && (
                <span>{storageData.path}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pod Details Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Pod Status</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ready</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restarts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {healthData?.pods?.details?.map(pod => (
                <tr key={pod.name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono">{pod.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center">
                      <StatusIndicator status={pod.status} />
                      <span className="ml-2">{pod.status}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{pod.ready}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={pod.restarts > 0 ? 'text-yellow-600 font-medium' : ''}>
                      {pod.restarts}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatAge(pod.age_seconds)}</td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => setSelectedPod(selectedPod === pod.name ? null : pod.name)}
                      className={`text-blue-600 hover:text-blue-800 ${
                        selectedPod === pod.name ? 'font-semibold' : ''
                      }`}
                    >
                      {selectedPod === pod.name ? 'Hide Logs' : 'View Logs'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pod Logs Panel */}
      {selectedPod && (
        <div className="bg-gray-900 rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">
              Logs: {selectedPod}
            </h3>
            <button
              onClick={() => setSelectedPod(null)}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {logsLoading ? (
              <div className="text-gray-400">Loading logs...</div>
            ) : podLogs?.logs ? (
              <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
                {podLogs.logs.join('\n')}
              </pre>
            ) : (
              <div className="text-gray-400">No logs available</div>
            )}
          </div>
        </div>
      )}

      {/* Deployment Details */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Deployments</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Desired</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ready</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {healthData?.deployments?.details?.map(dep => (
                <tr key={dep.name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{dep.name}</td>
                  <td className="px-4 py-3 text-sm">{dep.desired}</td>
                  <td className="px-4 py-3 text-sm">{dep.available}</td>
                  <td className="px-4 py-3 text-sm">{dep.ready}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      dep.healthy
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {dep.healthy ? 'Healthy' : 'Degraded'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => restartDeployment(dep.name)}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={actionLoading}
                    >
                      Restart
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Storage Configuration */}
      {storageData && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Storage Configuration</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Storage Provider Info */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Provider Details</h4>
                <dl className="space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Type:</dt>
                    <dd className="text-sm font-medium">{storageData.type?.toUpperCase()}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Provider:</dt>
                    <dd className="text-sm font-medium">{storageData.provider}</dd>
                  </div>
                  {storageData.bucket && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Bucket:</dt>
                      <dd className="text-sm font-medium font-mono">{storageData.bucket}</dd>
                    </div>
                  )}
                  {storageData.project && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Project:</dt>
                      <dd className="text-sm font-medium">{storageData.project}</dd>
                    </div>
                  )}
                  {storageData.endpoint && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Endpoint:</dt>
                      <dd className="text-sm font-medium">{storageData.endpoint}</dd>
                    </div>
                  )}
                  {storageData.region && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Region:</dt>
                      <dd className="text-sm font-medium">{storageData.region}</dd>
                    </div>
                  )}
                  {storageData.path && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Path:</dt>
                      <dd className="text-sm font-medium font-mono">{storageData.path}</dd>
                    </div>
                  )}
                  {storageData.credentials && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Credentials:</dt>
                      <dd className={`text-sm font-medium ${
                        storageData.credentials === 'Configured' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {storageData.credentials}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* PVC Status */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Persistent Volume Claims</h4>
                {storageData.pvcs?.length > 0 ? (
                  <div className="space-y-2">
                    {storageData.pvcs.map((pvc, idx) => (
                      pvc.error ? (
                        <div key={idx} className="text-sm text-red-600">{pvc.error}</div>
                      ) : (
                        <div key={pvc.name} className="p-2 bg-gray-50 rounded text-sm">
                          <div className="font-medium">{pvc.name}</div>
                          <div className="flex justify-between text-gray-600">
                            <span>Status:</span>
                            <span className={pvc.status === 'Bound' ? 'text-green-600' : 'text-yellow-600'}>
                              {pvc.status}
                            </span>
                          </div>
                          <div className="flex justify-between text-gray-600">
                            <span>Capacity:</span>
                            <span>{pvc.capacity}</span>
                          </div>
                          {pvc.storage_class && (
                            <div className="flex justify-between text-gray-600">
                              <span>Class:</span>
                              <span>{pvc.storage_class}</span>
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No PVCs found in namespace</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Events */}
      {events?.events?.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Recent Kubernetes Events</h3>
          </div>
          <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
            {events.events.map((event, idx) => (
              <div key={idx} className="px-4 py-3">
                <div className="flex items-start">
                  <span className={`flex-shrink-0 w-2 h-2 mt-2 rounded-full ${
                    event.type === 'Warning' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <div className="ml-3 flex-1">
                    <div className="flex justify-between">
                      <span className="font-medium text-sm">{event.reason}</span>
                      <span className="text-xs text-gray-500">
                        {event.last_timestamp ? new Date(event.last_timestamp).toLocaleTimeString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{event.message}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {event.involved_object?.kind}: {event.involved_object?.name}
                      {event.count > 1 && ` (${event.count}x)`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfrastructureDashboard;
